// src/pages/AdminControlPage.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    getRecommendedStrategies, 
    createRecommendedStrategy, 
    deleteRecommendedStrategy,
    checkAdminAccess 
} from '../api/apiClient';
import { useStrategyWebSocket } from '../hooks/useStrategyWebSocket';
import { 
    HiPlus, HiTrash, HiShieldCheck, HiChartBar, 
    HiSave, HiRefresh, HiSearch, HiExclamationCircle, HiInformationCircle 
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { StrategyDisplayCard } from './StatsPage';

// --- Вспомогательные компоненты ---
const InputField = ({ label, name, type = 'text', value, onChange, step, placeholder, readOnly }) => (
  <div>
    <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">{label}</label>
    <input
      id={name} name={name} type={type} value={value} onChange={onChange}
      step={step} placeholder={placeholder} readOnly={readOnly}
      className={`w-full border border-slate-600/50 rounded-lg text-white p-2.5 text-sm outline-none transition-all 
        ${readOnly ? 'bg-slate-800/50 border-slate-700 text-slate-400 cursor-not-allowed' : 'bg-slate-700/50 focus:ring-2 focus:ring-blue-500/50'}`}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, customOptions }) => (
  <div>
    <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">{label}</label>
    <select
      id={name} name={name} value={value} onChange={onChange}
      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
    >
      {customOptions ? customOptions.map(opt => (
          <option key={opt.val} value={opt.val} className="bg-slate-800">{opt.label}</option>
      )) : options.map((opt) => (
        <option key={opt} value={opt} className="bg-slate-800">{opt}m</option>
      ))}
    </select>
  </div>
);

const SymbolAutocomplete = ({ label, name, value, onChange, options, placeholder, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!options) return;
    const term = String(value).toUpperCase().trim();
    setFilteredOptions(!term ? options : options.filter(opt => opt.includes(term)));
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">{label}</label>
      <div className="relative">
        <input
          name={name} type="text" value={value}
          onChange={(e) => { onChange({ target: { name, value: e.target.value.toUpperCase() } }); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={isLoading ? "..." : placeholder}
          className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-all uppercase"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500"><HiSearch className="w-4 h-4" /></div>
      </div>
      {isOpen && options.length > 0 && (
        <ul className="absolute z-[110] w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl max-h-40 overflow-y-auto custom-scrollbar">
          {filteredOptions.map((opt) => (
            <li key={opt} onMouseDown={() => { onChange({ target: { name, value: opt } }); setIsOpen(false); }} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white text-slate-300">{opt}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminControlPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isVerifying, setIsVerifying] = useState(true);
    const [recommended, setRecommended] = useState([]);
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [isLoadingSymbols, setIsLoadingSymbols] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    
    const [form, setForm] = useState({
        customStrategyName: '',
        description: '',
        symbol_a: 'BTCUSDT',
        symbol_b: 'ETHUSDT',
        timeframe: '15',
        zscore_period: '300',
        bar: '30000',
        margin: '100',
        leverage: '10',
        commission_rate: '0.1',
        exit: '0',
        shortLevels: '2, 3, 4',
        longLevels: '-2, -3, -4',
        risk_level: 'low'
    });

    const calculatedPositionSize = Number(form.margin || 0) * Number(form.leverage || 0);

    // ПОДГОТОВКА ОБЪЕКТА ДАННЫХ
    const getCleanParams = () => {
        const parseLevels = (str) => String(str).split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
        const comm = parseFloat(String(form.commission_rate).replace(',', '.')) || 0;

        return {
            symbol_a: form.symbol_a,
            symbol_b: form.symbol_b,
            timeframe: form.timeframe,
            zscore_period: Number(form.zscore_period),
            bar: Number(form.bar),
            margin: Number(form.margin),
            leverage: Number(form.leverage),
            risk_level: form.risk_level, // ЖЕСТКО ПЕРЕДАЕМ РИСК
            total_usdt_one_entry: calculatedPositionSize,
            strategy_name: "pairs_trading",
            strategy_method: "zscore",
            commission_rate: comm / 100,
            exit: Number(form.exit),
            level_entry_positive: parseLevels(form.shortLevels),
            level_entry_negative: parseLevels(form.longLevels),
            description: form.description
        };
    };

    const strategyParams = useMemo(() => getCleanParams(), [form, calculatedPositionSize]);
    const { data: previewData } = useStrategyWebSocket(strategyParams);

    // ПРЕДПРОСМОТР (теперь берет риск прямо из формы)
    const previewObject = useMemo(() => ({
        customStrategyName: form.customStrategyName || "Preview Strategy",
        parameters: strategyParams
    }), [form, strategyParams]);

    useEffect(() => {
        const init = async () => {
            try {
                const res = await checkAdminAccess();
                if (res.isAdmin) {
                    const [list, symbolsData] = await Promise.all([
                        getRecommendedStrategies(),
                        fetch('https://api.hedger.bar/api/candles/symbols?exchange=Bybit').then(r => r.json())
                    ]);
                    setRecommended(Array.isArray(list) ? list : []);
                    setAvailableSymbols(Array.isArray(symbolsData) ? symbolsData : (symbolsData.symbols || []));
                } else { navigate('/dashboard'); }
            } catch (e) { navigate('/dashboard'); } finally { setIsVerifying(false); }
        };
        init();
    }, [navigate]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setIsPublishing(true);
        try {
            await createRecommendedStrategy({
                strategyName: "pairs_trading",
                customStrategyName: form.customStrategyName,
                typeRecommendation: "Admin",
                parameters: getCleanParams()
            });
            const list = await getRecommendedStrategies();
            setRecommended(list);
            setForm(f => ({ ...f, customStrategyName: '', description: '' }));
            alert("Published!");
        } catch (e) { alert(e.message); } finally { setIsPublishing(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete?")) return;
        try {
            await deleteRecommendedStrategy(id);
            setRecommended(prev => prev.filter(s => s.publicStrategyId !== id));
        } catch (e) { alert("Error"); }
    };

    if (isVerifying) return <div className="p-20 text-center text-white animate-pulse">Verifying Access...</div>;

    return (
        <div className="space-y-8 pb-20">
            <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500 rounded-xl shadow-lg shadow-amber-900/20"><HiShieldCheck className="w-8 h-8 text-slate-900" /></div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tight">Admin Control</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-8 bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2 border-b border-slate-700 pb-4"><HiPlus className="text-blue-400" /> New Recommendation</h2>
                    
                    <form onSubmit={handleCreate} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2"><InputField label="Display Name" name="customStrategyName" value={form.customStrategyName} onChange={e => setForm({...form, customStrategyName: e.target.value})} /></div>
                            <SelectField 
                                label="Risk Badge" name="risk_level" value={form.risk_level} 
                                onChange={e => setForm({...form, risk_level: e.target.value})} 
                                customOptions={[
                                    { val: 'low', label: 'Low Risk (Green)' },
                                    { val: 'medium', label: 'Medium Risk (Amber)' },
                                    { val: 'high', label: 'High Risk (Red)' }
                                ]}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <SymbolAutocomplete label="Symbol A" name="symbol_a" value={form.symbol_a} onChange={e => setForm({...form, symbol_a: e.target.value})} options={availableSymbols} />
                            <SymbolAutocomplete label="Symbol B" name="symbol_b" value={form.symbol_b} onChange={e => setForm({...form, symbol_b: e.target.value})} options={availableSymbols} />
                            <SelectField label="Timeframe" name="timeframe" value={form.timeframe} onChange={e => setForm({...form, timeframe: e.target.value})} options={[1, 3, 5, 15, 30, 60]} />
                        </div>

                        <textarea className="w-full bg-slate-900 border border-slate-700 p-3 rounded-lg text-white h-24 resize-none outline-none text-sm italic" placeholder="Description..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} />

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <InputField label="Z-Period" name="zscore_period" type="number" value={form.zscore_period} onChange={e => setForm({...form, zscore_period: e.target.value})} />
                            <InputField label="Bar Count" name="bar" type="number" value={form.bar} onChange={e => setForm({...form, bar: e.target.value})} />
                            <InputField label="Margin (USDT)" name="margin" type="number" value={form.margin} onChange={e => setForm({...form, margin: e.target.value})} />
                            <InputField label="Leverage" name="leverage" type="number" value={form.leverage} onChange={e => setForm({...form, leverage: e.target.value})} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label="Calculated Pos Size" value={calculatedPositionSize} readOnly={true} />
                            <InputField label="Comm. Rate (%)" name="commission_rate" value={form.commission_rate} onChange={e => setForm({...form, commission_rate: e.target.value})} />
                        </div>

                        <InputField label="Exit (Z-Score)" name="exit" value={form.exit} onChange={e => setForm({...form, exit: e.target.value})} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label="Levels (+)" name="shortLevels" value={form.shortLevels} onChange={e => setForm({...form, shortLevels: e.target.value.replace(/[^0-9,. ]/g, '')})} />
                            <InputField label="Levels (-)" name="longLevels" value={form.longLevels} onChange={e => setForm({...form, longLevels: e.target.value.replace(/[^-0-9,. ]/g, '')})} />
                        </div>

                        <div className="p-5 bg-slate-900 border border-slate-700 rounded-2xl flex flex-wrap justify-between items-center gap-6">
                            <div className="flex gap-8">
                                <div><p className="text-[10px] text-slate-500 font-bold uppercase">Preview PnL</p><p className={`text-2xl font-black font-mono ${previewData?.summary?.net_total_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>${previewData?.summary?.net_total_pnl?.toFixed(2) || '0.00'}</p></div>
                                <div><p className="text-[10px] text-slate-500 font-bold uppercase">Trades</p><p className="text-2xl font-black font-mono text-white">{previewData?.summary?.total || 0}</p></div>
                            </div>
                            <button type="submit" disabled={isPublishing || !form.customStrategyName} className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-all"><HiSave /> Publish Strategy</button>
                        </div>
                    </form>
                </div>

                <div className="lg:col-span-4 sticky top-6 space-y-8">
                    <div><h2 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 ml-2">Live Preview</h2><StrategyDisplayCard strategy={previewObject} isPreview={true} /></div>
                    <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-xl overflow-hidden flex flex-col max-h-[400px]">
                        <div className="p-4 border-b border-slate-700 bg-slate-800/50 flex items-center gap-2"><HiChartBar className="text-purple-400" /><h2 className="text-sm font-bold text-white uppercase">Current Catalog</h2></div>
                        <div className="p-2 space-y-2 overflow-y-auto custom-scrollbar flex-1 bg-slate-900/30">
                            {recommended.map(s => (
                                <div key={s.publicStrategyId} className="p-3 bg-slate-900 border border-slate-700 rounded-xl flex justify-between items-center group hover:border-blue-500/50">
                                    <div className="min-w-0"><p className="text-white font-bold text-xs truncate">{s.customStrategyName}</p><p className="text-[10px] text-slate-500">{s.parameters?.symbol_a}/{s.parameters?.symbol_b}</p></div>
                                    <button onClick={() => handleDelete(s.publicStrategyId)} className="p-2 text-slate-600 hover:text-rose-500"><HiTrash className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminControlPage;